/**
 * SHER-905 — naming an exported analytics table.
 *
 * Five tickets (SHER-306, SHER-560, SHER-585 part iii, SHER-807, SHER-976) each assumed an
 * export that did not exist. Filed once, it is this: a table declares its columns, and the
 * same declaration produces the header and the cells.
 *
 * The CSV itself is NOT built here. `ExportColumn` and `buildTableCsv` live in
 * `src/utils/csvDownload.ts` beside the quoting, the CRLF and the BOM, because none of them
 * knows anything about analytics — the surgical waiting list builds its file from the same
 * pair. What is genuinely analytics-specific, and all that remains here, is the filename:
 * which filters an analytics panel has, and how they are spelled.
 *
 * WHY THE BROWSER AND NOT AN ENDPOINT. The analytics tables render columns the API provably
 * cannot: `analytics.controller.ts` ships patient ids for the client to intersect precisely
 * because "patient sex is encrypted and the API cannot read it", and the unique-outpatients
 * route returns ids and dates so the client can compute age. A server-side xlsx would not
 * fail loudly on those columns — it would silently ship a file with the sex split and the
 * demographics missing. The plaintext exists in exactly one place, the browser, so that is
 * where the file is assembled.
 */

/** The filters in force when the rows were fetched, named in the file so the file can be filed. */
export interface AnalyticsExportContext {
  /** Start of the active range. Any spelling is accepted; the filename normalises it. */
  from?: string;
  to?: string;
  /** The active service/department filter label, when the table is scoped to one. */
  service?: string;
}

/** Accents, punctuation and runs of whitespace all collapse to single hyphens. */
function slugify(value: string): string {
  return value
    // Decompose, then drop the combining marks: "détaillée" -> "detaillee".
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Name the file after the table and the filters that produced it.
 *
 * A clinician exporting the same panel for three different quarters ends up with three files
 * in one downloads folder, and "diagnoses.csv", "diagnoses (1).csv", "diagnoses (2).csv" is
 * not a record of anything. The context segment is omitted entirely when the caller has no
 * filters to report, rather than emitting empty separators.
 *
 * EVERY segment goes through `slugify`, the dates included. This used to be a documented
 * request — "ISO reads best in a filename" — on a field typed `string`, which is not a
 * constraint but a hope: a caller handing over a range formatted for a French display,
 * `01/09/2026`, produced `diagnoses_01/09/2026_….csv`, and those slashes are path separators
 * that a browser silently rewrites when it saves the file. Slugifying is idempotent on an
 * ISO date (`2026-01-01` is already hyphens and digits), so callers that were already doing
 * the right thing see no change, and callers that were not can no longer break the filename.
 */
export function analyticsExportFilename(title: string, context: AnalyticsExportContext = {}): string {
  const segments = [title, context.from, context.to, context.service]
    .filter((segment): segment is string => Boolean(segment))
    .map(slugify)
    .filter((segment) => segment.length > 0);

  return `${segments.join('_')}.csv`;
}
